import React, { useState, useEffect } from 'react';
import { Users, Calendar } from 'lucide-react';
import MembershipChart from './MembershipChart';
import DateRangePicker from '../ui/DateRangePicker';
import { getSegmentChangeHistoryRequest } from 'store/segmentChangeHistory';
import { useDispatch } from 'react-redux';
import { ISegment } from 'types';

interface ActivityTabProps {
  segment?: ISegment;
}

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 29);
  return { startDate, endDate };
};

const formatDateForApi = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ActivityTab: React.FC<ActivityTabProps> = ({ segment }) => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(getDefaultDateRange());

  const handleDateRangeChange = (newDateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    setDateRange(newDateRange);
  };

  useEffect(() => {
    if (!segment?._id || !dateRange.startDate || !dateRange.endDate) return;
    dispatch(
      getSegmentChangeHistoryRequest({
        segmentId: segment._id,
        startDate: formatDateForApi(dateRange.startDate),
        endDate: formatDateForApi(dateRange.endDate),
      })
    );
  }, [segment?._id, dateRange.startDate, dateRange.endDate, dispatch]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-foreground">
            Membership Activity
          </h3>
          <p className="text-sm text-muted-foreground">
            Track segment membership changes and growth over time
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="w-3.5 h-3.5 shrink-0" />
            <span className="font-medium">
              {segment?.contacts?.length || 0} members
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground shrink-0">
              Date range
            </span>
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder="Select range"
              className="min-w-[240px]"
            />
          </div>
        </div>
      </div>

      {/* Membership Chart Card */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <MembershipChart segment={segment} />
      </div>
    </div>
  );
};

export default ActivityTab;
